import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { FieldRow, TextInput } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { searchOrders } from "@/lib/projectOverview";
import { useProjectOverview } from "@/projectOverview/useProjectOverview";
import type { AuftragLookup } from "@/lib/material";
import {
  createAblage,
  DEFAULT_PLANUNG_COLOR,
  PLANUNG_COLORS,
  updateAblage,
} from "@/lib/montageplanung";
import type { PlanungAblageInput, PlanungAblageItem, PlanungOrderLink } from "@/lib/montageplanung";
import { splitOrderLinkLabel } from "./displayLabels";
import { AssignmentFlagCard } from "./AssignmentDialog";
import { MontageunterlageNoteComposer } from "./AssignmentMontageunterlagen";
import { MP_DIALOG_Z_INDEX_CLASS } from "./layoutConstants";

function toAuftragLookup(order: {
  id: number;
  auftragsnummer?: string;
  kunde?: string;
  beschreibung?: string;
  jahr?: number;
  ort?: string;
}): AuftragLookup {
  return {
    id: order.id,
    auftragsnummer: order.auftragsnummer?.trim() || String(order.id),
    jahr: order.jahr ?? null,
    kunde: order.kunde ?? null,
    ort: order.ort ?? null,
    beschreibung: order.beschreibung ?? null,
    ort_zusatz: null,
  };
}

function LinkedOrderOverviewButton({ orderId }: { orderId: number }) {
  const { open } = useProjectOverview();
  return (
    <button
      type="button"
      className="inline-flex h-8 shrink-0 items-center gap-1.5 rounded-lg border border-separator bg-card px-2 text-xs font-medium text-label transition hover:bg-fill-tertiary"
      title="Projektübersicht öffnen"
      onClick={(event) => {
        event.stopPropagation();
        open(orderId);
      }}
    >
      <img
        src="/static/icons/apple-touch-icon-180.png"
        alt=""
        width={16}
        height={16}
        className="object-contain"
        decoding="async"
      />
      Übersicht
    </button>
  );
}

function parseCount(value: string): number | null {
  const count = Number.parseInt(value, 10);
  if (!Number.isFinite(count) || count < 1) return null;
  return count;
}

export function AblageFormDialog({
  open,
  item,
  onClose,
  onSaved,
}: {
  open: boolean;
  item: PlanungAblageItem | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState("");
  const [personCount, setPersonCount] = useState("2");
  const [dayCount, setDayCount] = useState("1");
  const [color, setColor] = useState(DEFAULT_PLANUNG_COLOR);
  const [isFixed, setIsFixed] = useState(false);
  const [isBlocker, setIsBlocker] = useState(false);
  const [orders, setOrders] = useState<PlanungOrderLink[]>([]);
  const [note, setNote] = useState("");
  const [orderQuery, setOrderQuery] = useState("");
  const [orderResults, setOrderResults] = useState<AuftragLookup[]>([]);
  const [orderSearching, setOrderSearching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const searchTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setOrderQuery("");
    setOrderResults([]);
    setTitle(item?.title ?? "");
    setPersonCount(String(item?.person_count ?? 2));
    setDayCount(String(item?.day_count ?? 1));
    setColor(item?.color || DEFAULT_PLANUNG_COLOR);
    setIsFixed(item?.is_fixed ?? false);
    setIsBlocker(item?.is_blocker ?? false);
    setOrders(item?.orders ?? []);
    setNote(item?.note ?? "");
  }, [open, item]);

  useEffect(() => {
    if (!open) return;
    const query = orderQuery.trim();
    if (searchTimer.current) window.clearTimeout(searchTimer.current);
    if (query.length < 2) {
      setOrderResults([]);
      setOrderSearching(false);
      return;
    }
    setOrderSearching(true);
    searchTimer.current = window.setTimeout(async () => {
      try {
        const results = await searchOrders(query, 8);
        setOrderResults(results.map(toAuftragLookup));
      } catch {
        setOrderResults([]);
      } finally {
        setOrderSearching(false);
      }
    }, 250);
    return () => {
      if (searchTimer.current) window.clearTimeout(searchTimer.current);
    };
  }, [orderQuery, open]);

  const selectedOrderIds = useMemo(() => new Set(orders.map((order) => order.order_id)), [orders]);

  function addOrder(order: AuftragLookup) {
    if (selectedOrderIds.has(order.id)) return;
    setOrders((prev) => [
      ...prev,
      { order_id: order.id, order_label: formatAuftragLookupLabel(order) },
    ]);
    setOrderQuery("");
    setOrderResults([]);
  }

  function removeOrder(orderId: number) {
    setOrders((prev) => prev.filter((order) => order.order_id !== orderId));
  }

  async function handleSave() {
    const trimmed = title.trim();
    const persons = parseCount(personCount);
    const days = parseCount(dayCount);
    if (!trimmed) {
      setError("Bitte einen Titel eingeben.");
      return;
    }
    if (persons == null || persons > 40) {
      setError("Personenanzahl muss zwischen 1 und 40 liegen.");
      return;
    }
    if (days == null || days > 90) {
      setError("Tage müssen zwischen 1 und 90 liegen.");
      return;
    }
    const payload: PlanungAblageInput = {
      title: trimmed,
      person_count: persons,
      day_count: days,
      color,
      is_fixed: isFixed,
      is_blocker: isBlocker,
      note: note.trim() || null,
      orders,
    };
    setSaving(true);
    setError(null);
    try {
      if (item) await updateAblage(item.id, payload);
      else await createAblage(payload);
      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={item ? "Ablage bearbeiten" : "In die Ablage"}
      description="Dieselben Angaben wie beim Anlegen eines Einsatzes. Zeitraum und Zeilen entstehen erst beim Einplanen."
      size="xl"
      zIndexClass={MP_DIALOG_Z_INDEX_CLASS}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={() => void handleSave()} loading={saving}>
            {item ? "Speichern" : "In die Ablage"}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {error && <Alert variant="error">{error}</Alert>}

        <TextInput
          label="Titel"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder="z. B. DFS Wartung Center"
          maxLength={200}
          autoFocus
        />

        <div className="grid grid-cols-2 gap-3">
          <TextInput
            label="Personen"
            type="number"
            min={1}
            max={40}
            inputMode="numeric"
            value={personCount}
            onChange={(event) => setPersonCount(event.target.value)}
          />
          <TextInput
            label="Tage"
            type="number"
            min={1}
            max={90}
            inputMode="numeric"
            value={dayCount}
            onChange={(event) => setDayCount(event.target.value)}
          />
        </div>
        <p className="text-xs leading-relaxed text-label-secondary">
          Tage sind Arbeitstage. Das Wochenende wird beim Einplanen übersprungen.
        </p>

        <FieldRow
          label={
            <span className="flex w-full items-baseline justify-between gap-3">
              <span>Farbe</span>
              <span className="text-xs font-normal text-label-secondary">
                {PLANUNG_COLORS.find((entry) => entry.value === color)?.label}
              </span>
            </span>
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            {PLANUNG_COLORS.map((entry) => {
              const selected = color === entry.value;
              return (
                <button
                  key={entry.value}
                  type="button"
                  title={entry.label}
                  aria-label={entry.label}
                  aria-pressed={selected}
                  onClick={() => setColor(entry.value)}
                  className={clsx(
                    "h-8 w-8 rounded-full transition focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-card",
                    selected
                      ? "ring-2 ring-label ring-offset-2 ring-offset-card"
                      : "ring-1 ring-black/10 hover:scale-105 dark:ring-white/20",
                  )}
                  style={{ backgroundColor: entry.value }}
                />
              );
            })}
          </div>
        </FieldRow>

        <div className="grid grid-cols-2 gap-2">
          <AssignmentFlagCard
            id="mp-ablage-fixed"
            tone="fixed"
            compact
            title="Fix"
            description=""
            checked={isFixed}
            onChange={setIsFixed}
          />
          <AssignmentFlagCard
            id="mp-ablage-blocker"
            tone="blocker"
            compact
            title="Blocker"
            description=""
            checked={isBlocker}
            onChange={setIsBlocker}
          />
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-label-tertiary">
            Aufträge
          </p>
          {orders.length > 0 && (
            <ul className="space-y-2">
              {orders.map((order) => {
                const { number, details } = splitOrderLinkLabel(order.order_id, order.order_label);
                return (
                  <li
                    key={order.order_id}
                    className="flex flex-wrap items-center gap-2 rounded-xl bg-fill-quaternary/70 px-3 py-2.5 ring-1 ring-inset ring-separator"
                  >
                    <div className="min-w-[10rem] flex-1">
                      <div className="truncate font-mono text-[13px] font-semibold tracking-tight text-label">
                        {number}
                      </div>
                      {details ? (
                        <div className="mt-0.5 line-clamp-2 text-xs leading-snug text-label-secondary">
                          {details}
                        </div>
                      ) : null}
                    </div>
                    <LinkedOrderOverviewButton orderId={order.order_id} />
                    <button
                      type="button"
                      className="inline-flex h-8 shrink-0 items-center rounded-lg px-2 text-xs font-medium text-label-secondary transition hover:bg-status-red/10 hover:text-status-red"
                      onClick={() => removeOrder(order.order_id)}
                    >
                      Entfernen
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="relative">
            <MagnifyingGlassIcon
              className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-label-tertiary"
              aria-hidden
            />
            <input
              className="eh-input !pl-9"
              value={orderQuery}
              onChange={(event) => setOrderQuery(event.target.value)}
              placeholder="Auftrag suchen und hinzufügen…"
              aria-label="Auftrag suchen und hinzufügen"
            />
            {(orderSearching || orderResults.length > 0) && orderQuery.trim().length >= 2 && (
              <div className="absolute z-10 mt-1 max-h-56 w-full overflow-auto rounded-xl border border-separator bg-card p-1 shadow-ios-md">
                {orderSearching && (
                  <div className="px-3 py-2 text-sm text-label-secondary">Suche…</div>
                )}
                {!orderSearching &&
                  orderResults
                    .filter((order) => !selectedOrderIds.has(order.id))
                    .map((order) => (
                      <button
                        key={order.id}
                        type="button"
                        className="block w-full rounded-lg px-3 py-2 text-left text-sm line-clamp-2 break-words hover:bg-fill-tertiary"
                        onClick={() => addOrder(order)}
                      >
                        {formatAuftragLookupLabel(order)}
                      </button>
                    ))}
                {!orderSearching &&
                  orderResults.filter((order) => !selectedOrderIds.has(order.id)).length === 0 && (
                    <div className="px-3 py-2 text-sm text-label-secondary">Keine Treffer</div>
                  )}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-label-tertiary">
            Hinweis für diesen Einsatz
          </p>
          <textarea
            className="eh-input min-h-[4.5rem]"
            rows={2}
            maxLength={4000}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="z. B. Morgens in der Werkstatt treffen"
            aria-label="Hinweis für diesen Einsatz"
          />
          <p className="text-xs leading-relaxed text-label-secondary">
            Gilt für den Einsatz und erscheint im Hover, sobald er eingeplant ist.
          </p>
        </div>

        <div className="space-y-2">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-label-tertiary">
            Montageunterlagennotiz
          </p>
          <MontageunterlageNoteComposer orders={orders} />
        </div>
      </div>
    </Dialog>
  );
}
